import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    fetchVendorProducts,
    deleteProduct,
} from "../redux/slices/productSlice";
import { useNavigate } from "react-router-dom";

const ProductList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { vendorProducts, vendorPagination, loading } = useSelector(
        (state) => state.product
    );
    const { user } = useSelector((state) => state.auth);

    const vendorId = user?.vendor?._id;

    /* ======================
       LOCAL STATE
    ====================== */
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const limit = 10;

    /* ======================
       FETCH PRODUCTS
    ====================== */
    useEffect(() => {
        if (vendorId) {
            dispatch(
                fetchVendorProducts({
                    vendorId,
                    params: {
                        page,
                        limit,
                        search,
                    },
                })
            );
        }
    }, [vendorId, page, search, dispatch]);

    /* ======================
       DELETE
    ====================== */
    const handleDelete = async (id) => {
        const res = await Swal.fire({
            title: "Delete product?",
            text: "This product will be deactivated",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            confirmButtonText: "Yes, delete",
        });

        if (!res.isConfirmed) return;

        try {
            await dispatch(deleteProduct(id)).unwrap();
            Swal.fire("Deleted", "Product deleted successfully", "success");
        } catch (err) {
            Swal.fire("Error", err, "error");
        }
    };

    /* ======================
       PAGINATION
    ====================== */
    const totalPages = vendorPagination?.totalPages || 1;

    return (
        <div className="container mt-4">
            <div className="card shadow-sm">
                <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center">
                    <h6 className="mb-0 text-white">Products</h6>
                    <button
                        className="btn btn-sm btn-light"
                        onClick={() => navigate("/add-product")}
                    >
                        + Add Product
                    </button>
                </div>

                <div className="card-body">
                    {/* 🔍 SEARCH */}
                    <div className="row mb-3">
                        <div className="col-md-4">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by name or SKU..."
                                value={search}
                                onChange={(e) => {
                                    setPage(1);
                                    setSearch(e.target.value);
                                }}
                            />
                        </div>
                    </div>

                    {/* LOADING */}
                    {loading && (
                        <div className="text-center py-4">Loading...</div>
                    )}

                    {/* EMPTY */}
                    {!loading && vendorProducts.length === 0 && (
                        <div className="text-center text-muted">
                            No products found
                        </div>
                    )}

                    {/* TABLE */}
                    {!loading && vendorProducts.length > 0 && (
                        <>
                            <div className="table-responsive">
                                <table className="table table-sm table-bordered table-hover align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>#</th>
                                            <th>Name</th>
                                            <th>SKU</th>
                                            <th>Category</th>
                                            <th>Price</th>
                                            <th>Stock</th>
                                            <th>Status</th>
                                            <th width="160">Actions</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {vendorProducts.map((p, index) => (
                                            <tr key={p._id}>
                                                <td>{(page - 1) * limit + index + 1}</td>
                                                <td>{p.name}</td>
                                                <td>{p.sku}</td>
                                                <td>
                                                    {p.category?.name}
                                                    {p.subCategory && (
                                                        <>
                                                            <br />
                                                            <small className="text-muted">
                                                                {p.subCategory.name}
                                                            </small>
                                                        </>
                                                    )}
                                                </td>
                                                <td>₹{p.price}</td>
                                                <td>
                                                    {p.stock <= p.minStockAlert ? (
                                                        <span className="text-danger fw-bold">
                                                            {p.stock}
                                                        </span>
                                                    ) : (
                                                        p.stock
                                                    )}
                                                </td>
                                                <td>
                                                    <span
                                                        className={`badge ${p.isActive ? "bg-success" : "bg-secondary"
                                                            }`}
                                                    >
                                                        {p.isActive ? "Active" : "Inactive"}
                                                    </span>
                                                </td>
                                                <td>
                                                    <button
                                                        className="btn btn-sm btn-primary me-2"
                                                        onClick={() =>
                                                            navigate(`/edit-product/${p._id}`)
                                                        }
                                                    >
                                                        Edit
                                                    </button>

                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => handleDelete(p._id)}
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* PAGINATION */}
                            <nav className="d-flex justify-content-end">
                                <ul className="pagination pagination-sm">
                                    <li className={`page-item ${page === 1 && "disabled"}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setPage(page - 1)}
                                        >
                                            Prev
                                        </button>
                                    </li>

                                    {[...Array(totalPages)].map((_, i) => (
                                        <li
                                            key={i}
                                            className={`page-item ${page === i + 1 ? "active" : ""
                                                }`}
                                        >
                                            <button
                                                className="page-link"
                                                onClick={() => setPage(i + 1)}
                                            >
                                                {i + 1}
                                            </button>
                                        </li>
                                    ))}

                                    <li
                                        className={`page-item ${page === totalPages && "disabled"
                                            }`}
                                    >
                                        <button
                                            className="page-link"
                                            onClick={() => setPage(page + 1)}
                                        >
                                            Next
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductList;
